import { useEffect, useState, type JSX } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { SEGMENTOS_ORCAMENTO } from '@orc/shared';
import { useAuth, type Papel } from '../auth/AuthContext.js';
import { CAMINHO_TROCA_SENHA, papeisAceitos } from '../auth/RotaProtegida.js';
import { slugAtual } from '../lib/tenant.js';
import './Layout.css';

/**
 * Casca da aplicação — Story 8.1.
 *
 * Navegação à esquerda e sempre visível: numa ferramenta de trabalho, o usuário
 * troca de área o tempo todo, e esconder o menu atrás de um botão custa um
 * clique por troca, o dia inteiro.
 *
 * O menu mostra **só o que o papel abre**. Item que leva a 403 é ruído.
 */

interface ItemNav {
  para: string;
  rotulo: string;
  /**
   * Quem vê o item. Omitido: todo mundo.
   *
   * Era `somenteAdmin?: boolean` até a Story 2.3. O dashboard é de **admin e
   * gestor**, e um booleano não sabe dizer isso — a saída fácil seria um
   * `somenteGestor` ao lado, e aí duas flags passam a poder se contradizer
   * (`somenteAdmin: true, somenteGestor: true` significa o quê?). Uma lista de
   * papéis responde às duas perguntas e a qualquer terceira.
   */
  papeis?: readonly Papel[];
  /** Ainda não implementado — aparece desabilitado, com a razão no title. */
  emBreve?: boolean;
}

const ITENS: ItemNav[] = [
  /*
   * O dashboard é o primeiro item para quem o vê: é a tela de "como estamos
   * indo", e quem abre o sistema de manhã abre por ela. Para o vendedor o item
   * não existe, e a lista começa em Orçamentos — que é o trabalho dele.
   *
   * Restrito porque mostra **RT**, a comissão do representante. Foi por isso
   * que o RT saiu do PDF no item 01 do feedback, e foi por isso que a Story 2.1
   * tirou `ver_dashboard` do vendedor.
   */
  { para: '/dashboard', rotulo: 'Dashboard', papeis: ['admin', 'gestor'] },
  { para: '/orcamentos', rotulo: 'Orçamentos' },
  /*
   * Del credere entra como ENTRADA PRÓPRIA, não como opção no meio do
   * gerador — foi o pedido do cliente ("opção à parte, não misturar com
   * RT10/CPF"). O que é exclusivo é a porta e o seletor de coluna; o
   * gerador de orçamento por trás é o MESMO, num modo diferente.
   */
  { para: '/del-credere', rotulo: 'Del Credere' },
  /*
   * Vinílicos e Rodapé Engenharia — S5, e pelo mesmo pedido do del credere:
   * "igual à tela separada de orçamentos com o nome Del Credere".
   *
   * Derivados de `SEGMENTOS_ORCAMENTO` em vez de escritos aqui: rota e rótulo
   * saem da mesma lista que o servidor usa para validar o segmento, e um menu
   * que apontasse para uma seção que o backend não conhece seria um link para
   * uma tela que recusa criar.
   *
   * Ficam junto do Del Credere porque é a vizinhança dos fluxos de orçamento —
   * as três são portas para o mesmo gerador.
   */
  ...SEGMENTOS_ORCAMENTO.map((s) => ({ para: s.rota, rotulo: s.rotulo })),
  { para: '/catalogo', rotulo: 'Catálogo' },
  { para: '/importacoes', rotulo: 'Tabelas de preço', papeis: ['admin'] },
  /*
   * Atividades — Frente L, Story L.4. **Só admin**, e por um recorte mais
   * estreito que o do dashboard: a trilha mostra ação administrativa e o e-mail
   * de quem errou a senha. No servidor, quem barra é a capacidade `ver_logs`.
   *
   * Por último na lista porque é a tela que menos se visita — abre-se para
   * investigar algo, não para trabalhar.
   */
  /*
   * Usuários — Frente 1, Story 1.5. Admin **e** gestor: `gerenciar_usuarios`
   * é dos dois desde a 1.1, e é o gestor quem cuida da equipe no dia a dia.
   *
   * Fica antes de Atividades porque se usa mais: cadastrar vendedor é rotina;
   * abrir a trilha é investigação.
   */
  { para: '/usuarios', rotulo: 'Usuários', papeis: ['admin', 'gestor'] },
  { para: '/logs', rotulo: 'Atividades', papeis: ['admin'] },
];

/**
 * Como cada papel se chama na tela.
 *
 * Era `role === 'admin' ? 'Administrador' : 'Vendedor'`, e passou a mentir na
 * Story 1.3, quando a API começou a criar gestores: o dono da empresa aparecia
 * como "Vendedor". Mapa em vez de ternário para papel novo não cair no ramo
 * errado por omissão.
 */
const ROTULO_PAPEL: Record<Papel, string> = {
  admin: 'Administrador',
  gestor: 'Gestor',
  vendedor: 'Vendedor',
};

export function Layout(): JSX.Element {
  const { usuario, sair } = useAuth();
  const slug = slugAtual();

  /*
   * O filtro usa o MESMO `papeisAceitos` do porteiro de rota, de propósito: o
   * menu não é segurança — quem digitar a URL na mão continua barrado lá, e a
   * API responde 403 de qualquer forma. O que o menu não pode é MENTIR,
   * mostrando uma porta que leva a "sem permissão".
   *
   * Sem usuário, a lista fica só com o que é de todo mundo. Isso não chega a
   * acontecer (o Layout mora dentro da `RotaProtegida`), mas a guarda de
   * `undefined` fecha por omissão em vez de abrir — um estado transitório não
   * mostra item restrito nem por um frame.
   */
  /*
   * O menu do celular abre e fecha — Frente 3, Story 2.
   *
   * A Story 1 tentou `flex-wrap` e o resultado embaralhou: marca, lista e
   * tenant no mesmo container em linha, com a lista pedindo `flex: 1`, cada um
   * caiu num pedaço torto da primeira faixa.
   *
   * Empilhar em três faixas resolveria o embaralho, mas custaria ~180px fixos
   * no topo de TODA tela — 27% de um iPhone SE, permanentes. O botão custa 56px
   * e só ocupa espaço quando alguém pede.
   *
   * O estado NÃO controla renderização: a lista está sempre no DOM e quem a
   * esconde é o CSS, e só abaixo de 900px. No desktop o menu lateral continua
   * exatamente como era, e o botão nem aparece.
   */
  const [menuAberto, setMenuAberto] = useState(false);
  const local = useLocation();

  // Navegou: fecha. Sem isto o menu fica aberto por cima da tela que a pessoa
  // acabou de escolher, e ela precisa fechá-lo para ver o que pediu.
  useEffect(() => {
    setMenuAberto(false);
  }, [local.pathname]);

  const papel = usuario?.role;
  const visiveis = ITENS.filter(
    (i) =>
      i.papeis === undefined || (papel !== undefined && papeisAceitos(i.papeis).includes(papel)),
  );

  return (
    <div className="casca">
      <nav className={menuAberto ? 'nav nav--aberto' : 'nav'} aria-label="Navegação principal">
        <div className="nav__marca">
          <span className="nav__marca-nome">Orçamentos</span>
          <span className="nav__marca-sub">Revestimentos</span>
          <button
            type="button"
            className="nav__botao"
            aria-expanded={menuAberto}
            aria-controls="nav-lista"
            onClick={() => {
              setMenuAberto((a) => !a);
            }}
          >
            {menuAberto ? 'Fechar' : 'Menu'}
          </button>
        </div>

        <ul className="nav__lista" id="nav-lista">
          {visiveis.map((item) => (
            <li key={item.para}>
              {item.emBreve === true ? (
                <span
                  className="nav__item nav__item--inativo"
                  title="Em construção — entra nas próximas stories do Épico 8"
                  aria-disabled="true"
                >
                  {item.rotulo}
                  <span className="nav__tag">em breve</span>
                </span>
              ) : (
                <NavLink
                  to={item.para}
                  className={({ isActive }) =>
                    isActive ? 'nav__item nav__item--ativo' : 'nav__item'
                  }
                >
                  {item.rotulo}
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        <div className="nav__rodape">
          {slug !== undefined && <span className="nav__tenant">{slug}</span>}
        </div>
      </nav>

      <div className="conteudo">
        <header className="header">
          <div className="header__usuario">
            <span className="header__nome">{usuario?.nome ?? '—'}</span>
            <span className="header__papel">{ROTULO_PAPEL[usuario?.role ?? 'vendedor']}</span>
          </div>
          <Link className="botao botao--discreto" to={CAMINHO_TROCA_SENHA}>
            Trocar senha
          </Link>
          <button
            type="button"
            className="botao botao--discreto"
            onClick={() => {
              void sair();
            }}
          >
            Sair
          </button>
        </header>

        <main className="principal">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
